import { type FC, type PropsWithChildren, useCallback, useMemo } from 'react';

import { clientAuth, profileQueryOptions } from '@psp-aggregator/sdk';
import { useQuery } from '@tanstack/react-query';

import type { AuthContext } from './types';

import { authContext } from './AuthContext';

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const hasToken = !!clientAuth.getStoredJwt();

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery(profileQueryOptions(hasToken));

  const isAuthenticated = !!user;

  const login = useCallback(
    async (email: string, password: string) => {
      await clientAuth.authWithCredentials(email, password);
      await refetch();
    },
    [refetch],
  );

  const logout = useCallback(async (onSuccess?: VoidFunction) => {
    await clientAuth.logout();
    onSuccess?.();
  }, []);

  const value = useMemo<AuthContext>(
    () => ({
      isAuthenticated,
      isLoading,
      login,
      logout,
      refetchUser: refetch,
      user,
    }),
    [isAuthenticated, isLoading, login, logout, refetch, user],
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};

AuthProvider.displayName = 'AuthProvider';
